import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Eye, FileText, FileDown, Loader2, CheckCircle } from 'lucide-react'
import { useTemplate } from '../../hooks/useTemplates'
import { previewTemplate, renderDocxTemplate } from '../../services/templatesApi'
import { VariableForm } from './VariableForm'

interface TemplateDetailProps {
  templateId: number
  onBack: () => void
  organizationId?: string
}

/**
 * Template detail view — fill variables, then open as Word document.
 *
 * Flow: select template → fill variables → generate DOCX → insert into Word.
 * Like Word's built-in template experience.
 */
export function TemplateDetail({ templateId, onBack, organizationId }: TemplateDetailProps) {
  const { t } = useTranslation()
  const { data: template, isLoading, error } = useTemplate(templateId, organizationId)

  const [values, setValues] = useState<Record<string, string | number | boolean>>({})
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [isGenerating, setIsGenerating] = useState(false)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isDone, setIsDone] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)

  const handleValueChange = useCallback((name: string, value: string | number | boolean) => {
    setValues((prev) => ({ ...prev, [name]: value }))
    setValidationErrors((prev) => {
      const next = { ...prev }
      delete next[name]
      return next
    })
  }, [])

  const validateVariables = useCallback((): boolean => {
    if (!template) return false
    const errors: Record<string, string> = {}

    for (const v of template.variables) {
      const val = values[v.name]

      if (v.is_required && (val === undefined || val === '' || val === null)) {
        errors[v.name] = t('common.required')
      }

      if (v.field_type === 'email' && val && typeof val === 'string') {
        if (!val.includes('@')) {
          errors[v.name] = t('templates.invalidEmail')
        }
      }

      if (v.min_value !== null && typeof val === 'number' && val < v.min_value) {
        errors[v.name] = t('templates.valueTooLow', { min: v.min_value })
      }

      if (v.max_value !== null && typeof val === 'number' && val > v.max_value) {
        errors[v.name] = t('templates.valueTooHigh', { max: v.max_value })
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [template, values, t])

  const handlePreview = useCallback(async () => {
    if (!template) return
    setIsPreviewing(true)
    try {
      const blob = await previewTemplate(template.id, values, true)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch {
      // Preview failed
    } finally {
      setIsPreviewing(false)
    }
  }, [template, values])

  const handleOpenInWord = useCallback(async () => {
    if (!template || !validateVariables()) return

    setIsGenerating(true)
    setGenError(null)

    try {
      // Fetch rendered DOCX from backend
      const blob = await renderDocxTemplate(template.id, values, organizationId)
      const arrayBuffer = await blob.arrayBuffer()
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
      )

      // Insert into the active Word document
      await Word.run(async (context) => {
        const body = context.document.body
        // Clear current content and insert the template DOCX
        body.clear()
        body.insertFileFromBase64(base64, Word.InsertLocation.replace)
        await context.sync()
      })

      setIsDone(true)

      // Return to template list after a short delay
      setTimeout(() => onBack(), 1500)
    } catch (err: any) {
      console.error('[KobSign] Failed to open template in Word:', err)
      setGenError(err.message || t('common.error'))
    } finally {
      setIsGenerating(false)
    }
  }, [template, values, organizationId, validateVariables, onBack, t])

  // Loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center flex-1 text-sm text-neutral-400">
        {t('common.loading')}
      </div>
    )
  }

  // Error
  if (error || !template) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-6 text-center">
        <p className="text-sm text-error-500 mb-2">{t('common.error')}</p>
        <button onClick={onBack} className="text-xs text-primary-600 hover:underline">
          {t('common.back')}
        </button>
      </div>
    )
  }

  // Success state
  if (isDone) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-6 text-center">
        <CheckCircle size={36} className="text-success-500 mb-3" />
        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
          {t('templates.insertedIntoWord', 'Template applied to document')}
        </p>
      </div>
    )
  }

  const hasVariables = (template.variables?.length ?? 0) > 0

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 border-b border-neutral-200 dark:border-neutral-700">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 mb-1"
        >
          <ArrowLeft size={12} />
          {t('common.back')}
        </button>
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-primary-500 shrink-0" />
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 truncate">
              {template.name}
            </h2>
            {template.category && (
              <span className="text-[10px] text-neutral-400">{template.category}</span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {hasVariables && (
          <>
            <h3 className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
              {t('templates.variables')}
            </h3>
            <VariableForm
              variables={template.variables}
              values={values}
              onChange={handleValueChange}
              errors={validationErrors}
            />
          </>
        )}
        {!hasVariables && (
          <p className="text-xs text-neutral-400 text-center py-4">
            {t('templates.noVariables')}
          </p>
        )}

        {genError && (
          <p className="text-xs text-error-500 mt-2">{genError}</p>
        )}
      </div>

      {/* Actions */}
      <div className="px-3 py-2 border-t border-neutral-200 dark:border-neutral-700 space-y-1.5">
        <button
          onClick={handlePreview}
          disabled={isPreviewing}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50 transition-colors"
        >
          <Eye size={12} />
          {isPreviewing ? t('common.loading') : t('templates.preview')}
        </button>
        <button
          onClick={handleOpenInWord}
          disabled={isGenerating}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-md bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-colors"
        >
          {isGenerating ? (
            <>
              <Loader2 size={12} className="animate-spin" />
              {t('templates.generating', 'Generating...')}
            </>
          ) : (
            <>
              <FileDown size={12} />
              {t('templates.openInWord', 'Open in Word')}
            </>
          )}
        </button>
      </div>
    </div>
  )
}
